import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '@/hooks/redux';
import api from '@/services/api';

const ROUTE_PATTERNS: Array<{ regex: RegExp; pattern: string }> = [
  { regex: /^\/leagues\/[^/]+\/teams\/[^/]+\/analysis$/, pattern: '/leagues/:id/teams/:id/analysis' },
  { regex: /^\/leagues\/[^/]+\/analysis$/, pattern: '/leagues/:id/analysis' },
  { regex: /^\/analysis\/[^/]+\/[^/]+$/, pattern: '/analysis/:type/:id' },
  { regex: /^\/players\/[^/]+$/, pattern: '/players/:id' },
  { regex: /^\/leagues\/[^/]+$/, pattern: '/leagues/:id' },
];

function normalizePath(pathname: string): string {
  for (const { regex, pattern } of ROUTE_PATTERNS) {
    if (regex.test(pathname)) return pattern;
  }
  return pathname;
}

const BATCH_INTERVAL = 10_000; // 10 seconds

export function usePageTracking(): void {
  const location = useLocation();
  const { token } = useAppSelector((state) => state.auth);
  const bufferRef = useRef<string[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!token) return;

    const page = normalizePath(location.pathname);
    bufferRef.current.push(page);

    if (!timerRef.current) {
      timerRef.current = setTimeout(() => {
        flushBuffer(bufferRef.current);
        bufferRef.current = [];
        timerRef.current = null;
      }, BATCH_INTERVAL);
    }
  }, [location.pathname, token]);

  // Flush on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (bufferRef.current.length > 0) {
        flushBuffer(bufferRef.current);
        bufferRef.current = [];
      }
    };
  }, []);
}

function flushBuffer(pages: string[]): void {
  for (const page of pages) {
    api.post('/page-views', { page }).catch(() => {
      // Silently ignore tracking failures
    });
  }
}
